import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

const Section = ({ id, title, children }) => (
  <section id={id} className="mt-14 scroll-mt-24">
    <h2 className="font-display font-bold uppercase tracking-tight text-xl md:text-2xl text-[#f5f3ef]">{title}</h2>
    <div className="mt-5 text-sm md:text-base text-[#a3a3ac] font-light leading-relaxed space-y-3">{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="list-none space-y-1.5">
    {items.map((i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-[0.6em] block w-4 h-px bg-[#38bdf8]/60 shrink-0" />
        {i}
      </li>
    ))}
  </ul>
);

export default function Datenschutz() {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "Datenschutz | TONE";
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute("content", "Datenschutzerklärung der offiziellen Website von TONE.");
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
    return () => {
      document.title = "TONE — Emotional Techno";
      if (meta && prev) meta.setAttribute("content", prev);
    };
  }, [hash]);

  return (
    <main data-testid="datenschutz-page" className="min-h-screen bg-[#070709] text-[#f5f3ef] px-6 md:px-16 py-28">
      <div className="noise-page grain" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-3xl mx-auto"
      >
        <p className="font-mono2 text-[10px] tracking-[0.4em] text-[#64646e] uppercase">TONE — RECHTLICHES</p>
        <h1 className="font-display font-extrabold uppercase tracking-[-0.04em] leading-none text-[7vw] sm:text-[2.6rem] lg:text-6xl mt-4">
          DATEN<span className="text-[#70c7fa]">SCHUTZ</span>
        </h1>
        <p className="mt-6 font-mono2 text-[11px] tracking-[0.25em] text-[#38bdf8] uppercase">
          Informationen zum Umgang mit personenbezogenen Daten auf dieser Website
        </p>

        <p className="mt-10 font-mono2 text-[10px] tracking-[0.3em] text-[#64646e] uppercase">Datenschutzerklärung — Stand: September 2026</p>

        <Section title="1. Verantwortlicher">
          <p>
            Verantwortlich für die Verarbeitung personenbezogener Daten auf dieser Website ist:
          </p>
          <p>
            TONE / Toni Sven Beier
            <br />
            In der Lohe 4, 90765 Fürth, Deutschland
            <br />
            E-Mail:{" "}
            <a href="mailto:info@tonesound.de" className="text-[#70c7fa] hover:underline">
              info@tonesound.de
            </a>
          </p>
        </Section>

        <Section title="2. Allgemeine Hinweise">
          <p>
            Der Schutz deiner personenbezogenen Daten ist mir wichtig. Diese Datenschutzerklärung informiert darüber,
            welche personenbezogenen Daten beim Besuch dieser Website sowie bei der Nutzung der angebotenen
            Kontaktmöglichkeiten verarbeitet werden, zu welchen Zwecken dies geschieht und welche Rechte betroffenen
            Personen nach der Datenschutz-Grundverordnung (DSGVO) zustehen.
          </p>
        </Section>

        <Section title="3. Aufruf der Website und Server-Logfiles">
          <p>
            Beim Aufruf dieser Website können durch den Hosting-Anbieter technisch erforderliche Informationen
            verarbeitet werden. Dazu können insbesondere gehören:
          </p>
          <List
            items={[
              "IP-Adresse des zugreifenden Geräts",
              "Datum und Uhrzeit des Zugriffs",
              "aufgerufene Seiten und Dateien",
              "übertragene Datenmenge",
              "verwendeter Browser",
              "verwendetes Betriebssystem",
              "Referrer-URL",
              "technische Statusinformationen",
            ]}
          />
          <p>
            Die Verarbeitung erfolgt, soweit erforderlich, um die Website technisch bereitzustellen, deren Sicherheit
            und Stabilität zu gewährleisten sowie technische Fehler zu erkennen.
          </p>
          <p>
            Die Website wird über Emergent bereitgestellt. Im Rahmen des Hostings können technische Daten durch die
            hierfür eingesetzten Infrastruktur- und Hostingdienstleister verarbeitet werden.
          </p>
        </Section>

        <Section title="4. Kontaktformular">
          <p>
            Auf dieser Website besteht die Möglichkeit, über ein Kontaktformular mit TONE in Verbindung zu treten.
            Wenn das Kontaktformular genutzt wird, können insbesondere folgende Daten verarbeitet werden:
          </p>
          <List
            items={[
              "Name",
              "E-Mail-Adresse",
              "gegebenenfalls weitere freiwillig übermittelte Angaben",
              "Inhalt der Nachricht",
              "Zeitpunkt der Übermittlung",
              "technische Metadaten der Übermittlung",
            ]}
          />
          <p>
            Die Verarbeitung erfolgt zum Zweck der Bearbeitung der Anfrage und zur anschließenden Kommunikation.
          </p>
          <p>
            Die über das Kontaktformular übermittelten Informationen können zur Bearbeitung der Anfrage über
            verbundene Google-Dienste verarbeitet und gespeichert werden. Dabei können insbesondere Google Drive,
            Google Docs oder weitere Google-Workspace-Dienste eingesetzt werden.
          </p>
        </Section>

        <Section title="5. Externe Links">
          <p>
            Die Website enthält Links zu externen Plattformen und Diensten, insbesondere zu Musik- und
            Social-Media-Plattformen wie Spotify, SoundCloud und Instagram. Soweit es sich lediglich um normale
            externe Verlinkungen handelt, werden diese Inhalte nicht automatisch in die Website eingebettet.
          </p>
          <p>
            Beim Anklicken eines solchen Links wird die Website verlassen und der jeweilige externe Anbieter
            aufgerufen. Ab diesem Zeitpunkt gelten die Datenschutzbestimmungen des jeweiligen Anbieters.
          </p>
        </Section>

        <Section id="cookies" title="6. Cookies und vergleichbare Technologien">
          <p>
            Diese Website verwendet derzeit keine Cookies zu Analyse-, Werbe- oder Trackingzwecken. Derzeit werden
            insbesondere keine Analyse- oder Marketingtools wie Google Analytics eingesetzt.
          </p>
          <p>
            Eigene cookiefreie Reichweitenmessung: Diese Website nutzt eine eigene, cookiefreie Auswertung von
            Seitenaufrufen und Klicks (z. B. auf Buttons und Links). Dabei werden keine Cookies gesetzt, keine
            IP-Adressen gespeichert, keine Geräte wiedererkannt und keine Daten an Drittanbieter übertragen. Die
            Auswertung dient ausschließlich dazu zu verstehen, welche Inhalte der Website genutzt werden.
          </p>
          <p>
            Technisch notwendige Speicherung im Browser: Für den Login im nicht öffentlichen Admin-Bereich dieser
            Website wird nach erfolgreicher Anmeldung ein Sitzungs-Token im lokalen Speicher (localStorage) des
            verwendeten Browsers abgelegt. Dieser dient ausschließlich dem eingeloggten Zustand, enthält keine
            Tracking-Informationen, wird nicht an Dritte übertragen und betrifft nur die Person, die die Website
            verwaltet — nicht Besucher:innen der Website. Die Speicherung kann jederzeit über die
            Browser-Einstellungen gelöscht werden.
          </p>
          <p>
            Hinweis zur Hosting-Umgebung: Diese Website wird über die Plattform Emergent bereitgestellt. Im Rahmen
            des technischen Betriebs (z. B. Vorschau- und Verwaltungsumgebung) können durch die Plattform technisch
            notwendige Cookies oder vergleichbare Speicherungen gesetzt werden, auf deren Einsatz kein Einfluss
            besteht.
          </p>
          <p>
            Technisch erforderliche Datenverarbeitungen, die für den Betrieb und die Sicherheit der Website notwendig
            sind, bleiben hiervon unberührt.
          </p>
          <p>
            Sollten zukünftig zusätzliche Cookies oder vergleichbare Technologien eingesetzt werden, wird diese
            Datenschutzerklärung entsprechend aktualisiert und – soweit gesetzlich erforderlich – vor deren Einsatz
            eine Einwilligung eingeholt.
          </p>
        </Section>

        <Section title="7. Newsletter">
          <p>
            Über diese Website besteht die Möglichkeit, sich für den TONE Newsletter anzumelden. Die Anmeldung erfolgt
            freiwillig. Über den Newsletter können insbesondere Informationen zu neuen Releases, neuer Musik, Shows
            und Events sowie wichtigen TONE-News versendet werden.
          </p>
          <p>
            Für die Anmeldung ist grundsätzlich die Angabe der E-Mail-Adresse erforderlich. Die Anmeldung erfolgt im
            Double-Opt-in-Verfahren: Nach der Anmeldung erhältst du eine E-Mail mit einem Bestätigungslink. Erst nach
            der Bestätigung ist die Anmeldung abgeschlossen.
          </p>
          <p>
            Die Einwilligung kann jederzeit mit Wirkung für die Zukunft widerrufen werden — dafür genügt eine
            Nachricht an die oben genannte Kontaktadresse.
          </p>
          {/* PLATZHALTER: Sobald ein Newsletter-Anbieter feststeht, hier Anbieter-Name,
              Sitz und ggf. Auftragsverarbeitung ergänzen. */}
        </Section>

        <Section title="8. Speicherdauer">
          <p>
            Personenbezogene Daten werden grundsätzlich nur so lange gespeichert, wie dies für den jeweiligen Zweck
            erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
          </p>
        </Section>

        <Section title="9. Rechte betroffener Personen">
          <p>
            Betroffene Personen haben nach Maßgabe der gesetzlichen Voraussetzungen insbesondere folgende Rechte:
          </p>
          <List
            items={[
              "Recht auf Auskunft",
              "Recht auf Berichtigung",
              "Recht auf Löschung",
              "Recht auf Einschränkung der Verarbeitung",
              "Recht auf Datenübertragbarkeit",
              "Recht auf Widerspruch",
              "Recht auf Widerruf einer erteilten Einwilligung mit Wirkung für die Zukunft",
            ]}
          />
          <p>Zur Ausübung dieser Rechte kann die oben genannte Kontaktadresse verwendet werden.</p>
        </Section>

        <Section title="10. Beschwerderecht">
          <p>
            Es besteht das Recht, sich bei einer zuständigen Datenschutz-Aufsichtsbehörde über die Verarbeitung
            personenbezogener Daten zu beschweren.
          </p>
        </Section>

        <Section title="11. Aktualität">
          <p>
            Diese Datenschutzerklärung wird angepasst, wenn sich die technischen Funktionen der Website oder die
            rechtlichen Anforderungen ändern.
          </p>
          <p>Stand: September 2026</p>
        </Section>

        <Link
          data-testid="datenschutz-back-link"
          to="/"
          className="inline-block mt-16 font-mono2 text-[10px] tracking-[0.3em] text-[#64646e] hover:text-[#70c7fa] uppercase transition-colors"
        >
          ← Zurück zur Website
        </Link>
      </motion.div>
    </main>
  );
}
